import {
  createRef, useEffect, useRef, useState, RefObject,
} from 'react';
import { BsList, BsFillGridFill } from 'react-icons/bs';
import { useParams } from 'react-router';
import { t } from 'i18next';
import { Flex } from 'reflexbox';
import {
  HeaderContainer,
  ViewModeButton,
  SearchInput,
  Label,
  StyledSpan,
  MosaicContainer,
  StyledItem,
  PaginationWrapper,
  PageButton,
  PageButtonArrow,
} from './styles';
import SelectSearch, { fuzzySearch } from 'react-select-search';
import { Select, Accordion } from '~/components';
import { useStateVar } from '~/helpers/useStateVar';
import { ApiResps, apiCall } from '~/providers';
import { MosaicCard, MosaicCardProps } from './components/MosaicCard';
import { UtilityTable } from './components/UtilityTable';
import { useWebSocketLazy } from '~/helpers/wsConnection';
import {
  acessorUtilities, getAnalogicPort, treatGenerator, treatNobreak, treatTelemetryElevatorAndEscalator,
} from '~/pages/Analysis/DMTs/AcessorUtilities';
import {
  Content, StyledLine, StyledList, Wrapper,
} from '~/pages/Analysis/Header/styles';
import { colors } from '~/styles/colors';

const tabOrder = [
  'Illumination',
  'Nobreak',
  'Escalator',
  'Elevator',
  'Intrusion Alarm',
  'Fire Alarm',
  'Water Box',
  'Generator',
];
const ITEMS_PER_PAGE = 18;
export const UnitDetailUtilities = (
  props: {
    openScheduleDialogFor: (devId: string) => void;
  },
): JSX.Element => {
  const { openScheduleDialogFor } = props;
  const routeParams = useParams<{ unitId: string }>();
  const [mosaicView, setMosaicView] = useState(true);
  const [utilitiesVisualizationOrder, setUtilitiesVisualizationOrder] = useState(t('selecioneOrdemDeVisualizacao'));
  const [filter, setFilter] = useState<string[]>([]);
  const [state, render, setState] = useStateVar({
    unitId: Number(routeParams.unitId),
    isLoading: false,
    tab: 'Nobreak',
    startedWs: false,
    loadedData: false,
    illuminations: [] as any,
    utilitiesList: {} as ApiResps['/utilities/get-utilities-list-unit'],
    utilityList: [] as ApiResps['/utilities/get-utilities-list-unit']['all'],
    illuminationsList: [] as ApiResps['/dal/get-dal-illumination-list'],
    listTabs: [] as string[],
    dmtIds: [] as string[],
    dalIds: [] as string[],
    damIds: [] as string[],
    utilitiesListOpts: {} as Record<string, { name: string; value: string; }[]>,
    illuminationsListOpts: [] as { value: string, name: string }[],
    telemetries: {} as {[devId: string]: {
      RSSI?: number
    }},
  });
  const [currentPage, setCurrentPage] = useState(1);
  const [orderedUtilitiesList, setOrderedUtilitiesList] = useState<ApiResps['/utilities/get-utilities-list-unit']['all']>();
  const headerRefs = useRef<RefObject<HTMLUListElement>[]>(state.listTabs.map(() => createRef<HTMLUListElement>()));
  const ulRef = useRef(null);
  const wslNoBreak = useWebSocketLazy();
  const wslIllumination = useWebSocketLazy();
  const totalPages = Math.ceil((orderedUtilitiesList || []).length / ITEMS_PER_PAGE);
  const handleChangePage = (newPage) => {
    if (newPage >= 1 && newPage <= totalPages) {
      setCurrentPage(newPage);
    }
  };
  const currentItems = (orderedUtilitiesList || []).slice(
    (currentPage - 1) * ITEMS_PER_PAGE,
    currentPage * ITEMS_PER_PAGE,
  );
  function onWsOpen(wsConn) {
    if (state.dmtIds.length) {
      wsConn.send({ type: 'dmtSubscribeRealTime', data: { UNIT_ID: state.unitId } });
    }
    if (state.dalIds.length && state.tab === 'Illumination') {
      wsConn.send({ type: 'dalSubscribeRealTime', data: { UNIT_ID: state.unitId } });
    }
    if (state.damIds.length && state.tab === 'Illumination') {
      wsConn.send({ type: 'subscribeStatus', data: { unit_id: state.unitId } });
    }
  }

  function onWsMessage(response) {
    if (response && (response.type === 'dmtTelemetry' || response.type === 'dalTelemetry' || response.type === 'damStatus')) {
      state.telemetries[response.data.dev_id] = { ...response.data, safeWaitMode: false };
      render();
      getUtilitiesStatus();
    }
  }

  function beforeWsClose(wsConn) {
    wsConn.send({ type: 'dmtUnsubscribeRealTime' });
    wsConn.send({ type: 'dalUnsubscribeRealTime' });
    wsConn.send({ type: 'unsubscribeStatus' });
  }

  function getUtilitiesStatus() {
    if (state.tab) {
      state.utilityList = state.utilitiesList[state.tab]?.map((utility) => {
        const deviceCode = utility.DAL_CODE || utility.DMT_CODE || utility.DAM_ILLUMINATION_CODE;
        const telemetry = state.telemetries[deviceCode];
        return {
          ...utility,
          status: getUtilityStatus(
            { ...utility, DEVICE_CODE: deviceCode },
            telemetry,
          ),
          rssi: telemetry?.RSSI,
        };
      });
      render();
    }
  }

  function getUtilityStatus(
    utilityInfo: MosaicCardProps['utilityInfo'],
    telemetry: MosaicCardProps['telemetry'],
  ) {
    if (utilityInfo.PORT && utilityInfo.PORT_ELETRIC && telemetry?.status === 'ONLINE') {
      const electricNetworkPort = `F${utilityInfo.PORT_ELETRIC}`;
      const utilityPort = `F${utilityInfo.PORT}`;
      const utilityPort2 = `F${utilityInfo.PORT2}`;
      switch (utilityInfo.APPLICATION) {
        case 'Nobreak':
          return treatNobreak(telemetry[utilityPort], telemetry[electricNetworkPort]);
        case 'Illumination':
          return getIlluminationStatus(utilityInfo, telemetry);
        case 'Generator':
          return treatGenerator(telemetry[utilityPort], telemetry[utilityPort2], telemetry[electricNetworkPort]);
        case 'Escalator':
          return treatTelemetryElevatorAndEscalator(getAnalogicPort(`P${utilityInfo.PORT - 1}`, telemetry), telemetry[electricNetworkPort]);
        case 'Elevator':
          return treatTelemetryElevatorAndEscalator(getAnalogicPort(`P${utilityInfo.PORT - 1}`, telemetry), telemetry[electricNetworkPort]);
        case 'Water Box':
          return telemetry[utilityPort] ? 1 : 0;
        case 'Fire Alarm':
          return telemetry[utilityPort] ? 1 : 0;
        case 'Intrusion Alarm':
          return telemetry[utilityPort] ? 1 : 0;
        default:
          return null;
      }
    }
    return null;
  }

  useEffect(() => {
    getUtilitiesStatus();
  }, [state.tab]);

  const transformUtilitiesMap = (
    utilitiesMap: Record<string, any[]>,
  ): Record<string, { name: string; value: string }[]> => {
    const result: Record<string, { name: string; value: string }[]> = {};

    Object.entries(utilitiesMap).forEach(([key, utilities]) => {
      result[key] = utilities.map((utility) => ({
        name: utility.NAME,
        value: utility.ID.toString(),
      }));
    });

    return result;
  };

  function getIlluminationStatus(
    utilityInfo: MosaicCardProps['utilityInfo'],
    telemetry: MosaicCardProps['telemetry'],
  ) {
    if (telemetry?.status === 'ONLINE') {
      let illumStatus;
      if (telemetry.dev_id?.startsWith('DMT') && utilityInfo.PORT) {
        const illumPort = `F${utilityInfo.PORT}`;
        illumStatus = telemetry[illumPort];
      }
      if (telemetry.dev_id?.startsWith('DAL') && utilityInfo.FEEDBACK) {
        const illumPort = utilityInfo.FEEDBACK - 1;
        illumStatus = telemetry.Feedback?.[illumPort];
      }
      if (illumStatus != null) {
        return illumStatus ? 1 : 0;
      }
    }
    return null;
  }

  async function getUtilitiesInfo() {
    setState({ isLoading: true });
    try {
      await Promise.all([
        apiCall('/utilities/get-utilities-list-unit', { UNIT_ID: state.unitId }).then((list) => state.utilitiesList = list),
      ]);
      const dmtIds = [] as string[];
      const dalIds = [] as string[];
      const damIds = [] as string[];
      state.utilitiesList.all?.forEach((illum) => {
        if (illum.DMT_CODE) dmtIds.push(illum.DMT_CODE);
        if (illum.DAL_CODE) dalIds.push(illum.DAL_CODE);
        if (illum.DAM_ILLUMINATION_CODE) damIds.push(illum.DAM_ILLUMINATION_CODE);
      });

      if (state.utilitiesList?.Illumination) {
        state.illuminations = state.utilitiesList.Illumination.map((illumination) => ({ ...illumination, status: null }));
      }
      state.listTabs = tabOrder.filter((tab) => tab in state.utilitiesList && tab !== 'all');
      const hasIlluminations = state.illuminations.length > 0;

      if (hasIlluminations) {
        state.tab = 'Illumination';
      }
      if (state.utilitiesList) {
        state.utilitiesListOpts = transformUtilitiesMap(state.utilitiesList);
      }
      state.utilityList = state.utilitiesList[state.tab] || [];
      render();

      state.dmtIds = dmtIds;
      state.dalIds = dalIds;
      state.damIds = damIds;
    } catch (err) {
      console.log(err);
    }
    setState({ isLoading: false });
    state.loadedData = true;
    if (state.tab && !state.startedWs) {
      wslNoBreak.start(onWsOpen, onWsMessage, beforeWsClose);
      wslIllumination.start(onWsOpen, onWsMessage, beforeWsClose);
    }
    render();
    formatData();
  }

  function ascendingOrderStatus(a, b) {
    const statusA = a.status ?? -Infinity;
    const statusB = b.status ?? -Infinity;

    return statusB - statusA;
  }

  function descendingOrderStatus(a, b) {
    const statusA = a.status ?? -5;
    const statusB = b.status ?? 5;

    return statusA - statusB;
  }

  function sortingAlphabet(unorderedUtilities) {
    const orderedUtilitiesSort = [...unorderedUtilities].sort((a, b) => {
      const x = a.NAME.toLowerCase();
      const y = b.NAME.toLowerCase();

      return x < y ? -1 : x > y ? 1 : 0;
    });
    return orderedUtilitiesSort;
  }

  function sortingAscendingStatusUtility(unorderedUtilities) {
    const orderedUtilitiesSort = [...unorderedUtilities].sort((a, b) => ascendingOrderStatus(a, b));
    return orderedUtilitiesSort;
  }

  function sortingDescendingStatusUtility(unorderedUtilities) {
    const orderedUtilitiesSort = [...unorderedUtilities].sort((a, b) => descendingOrderStatus(a, b));
    return orderedUtilitiesSort;
  }

  function formatData() {
    let unorderedUtility = state.utilityList;
    let orderedUtilities = unorderedUtility;
    for (const [utilityType, list] of Object.entries(state.utilitiesList)) {
      if (filter.length > 0 && state.tab === utilityType) {
        unorderedUtility = list.filter((group) => filter.includes(group.ID.toString()));
        orderedUtilities = unorderedUtility;
      }

      if (utilitiesVisualizationOrder === t('ordemAlfabetica')) { orderedUtilities = sortingAlphabet(unorderedUtility); }
      else if (utilitiesVisualizationOrder === t('decrescenteStatus')) { orderedUtilities = sortingDescendingStatusUtility(unorderedUtility); }
      else { orderedUtilities = sortingAscendingStatusUtility(unorderedUtility); }
    }
    setOrderedUtilitiesList(orderedUtilities);
    render();
  }

  useEffect(() => {
    getUtilitiesInfo();
  }, []);

  useEffect(() => {
    formatData();
  }, [utilitiesVisualizationOrder, state.tab, filter]);

  useEffect(() => {
    if (state.loadedData) {
      if (state.tab && !state.startedWs) {
        wslNoBreak.start(onWsOpen, onWsMessage, beforeWsClose);
        wslIllumination.start(onWsOpen, onWsMessage, beforeWsClose);
        state.startedWs = true;
      }
    }
  }, [state.tab, state.loadedData]);

  function renderTabs() {
    return (
      <>
        {state.utilitiesList.all?.length > 0 && (
          <Wrapper>
            <Content>
              <StyledList ref={ulRef}>
                {
                  state.listTabs.map((tab, index) => {
                    const isActive = tab === state.tab;
                    if (isActive) {
                      handleScrollTo(headerRefs.current[index], ulRef);
                    }

                    return (
                      <StyledItem key={tab} isActive={isActive} onClick={() => { setFilter([]); setCurrentPage(1); setState({ ...state, utilityList: state.utilitiesList[tab], tab }); }}>
                        <span>{tab !== 'all' && acessorUtilities[tab].namePt}</span>
                        <StyledLine isActive={isActive} />
                      </StyledItem>
                    );
                  })
                }
              </StyledList>
            </Content>
          </Wrapper>
        )}
      </>
    );
  }
  if (!state.utilitiesList.all?.length) { return <></>; }
  return (
    <Accordion title={t('utilitarios')} opened>
      <>
        <HeaderContainer>
          <Flex>
            <ViewModeButton
              isActive={mosaicView}
              onClick={() => setMosaicView(true)}
            >
              <span style={{ width: 'max-content' }}>{t('mosaico')}</span>
              <BsFillGridFill style={{ width: '16px', height: '16px' }} />
            </ViewModeButton>
            <ViewModeButton
              isActive={!mosaicView}
              onClick={() => setMosaicView(false)}
            >
              <span style={{ width: 'max-content' }}>{t('lista')}</span>
              <BsList style={{ width: '16px', height: '16px' }} />
            </ViewModeButton>
          </Flex>

          <Flex style={{ width: '600px' }} flexWrap="wrap">
            <div
              style={{
                display: 'flex', flexDirection: 'column', height: 'auto', marginRight: '20px', width: '220px',
              }}
            >
              <SearchInput>
                <div style={{ width: 'fit-content' }}>
                  <Label>{t('buscar')}</Label>
                  <SelectSearch
                    options={state.utilitiesListOpts[state.tab] || []}
                    value={filter}
                    multiple
                    closeOnSelect={false}
                    printOptions="on-focus"
                    search
                    filterOptions={fuzzySearch}
                    placeholder={t('selecionarUtilitarios')}
                  // @ts-ignore
                    onChange={(newFilter) => setFilter(newFilter)}
                  />
                </div>
              </SearchInput>
            </div>
            <div style={{ width: '60%' }}>
              <Select
                placeholder={t('ordenarPor')}
                value={utilitiesVisualizationOrder}
                onSelect={(value) => {
                  setUtilitiesVisualizationOrder(value);
                }}
                options={[t('Default'), t('crescenteStatus'), t('decrescenteStatus'), t('ordemAlfabetica')]}
                hideSelected
              />
            </div>
          </Flex>
        </HeaderContainer>

        {renderTabs()}
        <div
          style={{
            width: '100%', display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '20px 0px 20px 0px',
          }}
        >
          <p style={{ fontSize: 16, fontWeight: 600, margin: 0 }}>{acessorUtilities?.[state.tab]?.namePt}</p>
          <span style={{ fontSize: 11, color: colors.text['soft-400'] }}>
            {state.utilitiesList?.[state.tab]?.length}
            {' '}
            {state.utilitiesList?.[state.tab]?.length > 1 ? acessorUtilities?.[state.tab]?.namePlural.toLowerCase() : acessorUtilities?.[state.tab]?.namePt.toLowerCase()}
            {' '}
            {t('disponiveis')}
          </span>
        </div>

        {mosaicView && (
          <>
            <MosaicContainer>
              {state.tab
              && (orderedUtilitiesList?.length ? currentItems.map((utility) => (
                <MosaicCard key={utility.ID} utilityInfo={{ ...utility, DEVICE_CODE: utility.DAL_CODE || utility.DMT_CODE || utility.DAM_ILLUMINATION_CODE }} telemetry={state.telemetries[utility.DAL_CODE || utility.DMT_CODE || utility.DAM_ILLUMINATION_CODE]} openScheduleDialogFor={() => openScheduleDialogFor(utility.DAL_CODE || utility.DMT_CODE || utility.DAM_ILLUMINATION_CODE)} />
              ))
                : <StyledSpan>{t('erroNaoForamEncontradosResultadosOuServico')}</StyledSpan>)}
            </MosaicContainer>
            {totalPages > 1 && (
              <PaginationUtility
                totalPages={totalPages}
                currentPage={currentPage}
                onPageChange={handleChangePage}
              />
            )}
          </>
        )}

        {!mosaicView && (
          <div style={{ marginLeft: 10 }}>
            {state.tab ? (
              <UtilityTable filterNobreaks={orderedUtilitiesList} telemetries={state.telemetries} tab={state.tab} />
            ) : <></>}
          </div>
        )}
      </>
    </Accordion>
  );
};

function handleScrollTo(currCont: { current }, ulRef) {
  try {
    if (currCont && currCont.current && ulRef && ulRef.current && window !== undefined && window.innerWidth < currCont.current.offsetLeft) {
      ulRef.current.scrollLeft = currCont.current.offsetLeft;
    }
  } catch (err) { console.log(err); }
}

type PaginationProps = {
  totalPages: number;
  currentPage: number;
  onPageChange: (page: number) => void;
};

export const PaginationUtility = ({ totalPages, currentPage, onPageChange }: PaginationProps) => {
  const goToPage = (page: number) => {
    if (page >= 1 && page <= totalPages) {
      onPageChange(page);
    }
  };

  return (
    <PaginationWrapper>
      <PageButtonArrow onClick={() => goToPage(1)} disabled={currentPage === 1}>«</PageButtonArrow>
      <PageButtonArrow onClick={() => goToPage(currentPage - 1)} disabled={currentPage === 1}>‹</PageButtonArrow>
      {[...Array(totalPages)].map((_, index) => {
        const page = index + 1;
        return (
          <PageButton
            key={page}
            active={currentPage === page}
            onClick={() => goToPage(page)}
          >
            {page}
          </PageButton>
        );
      })}
      <PageButtonArrow onClick={() => goToPage(currentPage + 1)} disabled={currentPage === totalPages}>›</PageButtonArrow>
      <PageButtonArrow onClick={() => goToPage(totalPages)} disabled={currentPage === totalPages}>»</PageButtonArrow>
    </PaginationWrapper>
  );
};
